import React from 'react';

/**
 * Highlights Component - Reusable component for emoji + text sections with colored backgrounds
 * 
 * @param {string} emoji - The emoji to display (e.g., "📦", "📋", "📝")
 * @param {React.ReactNode} children - The content/text to display
 * @param {string} color - Color theme: 'teal', 'blue', 'green', 'purple', 'amber', 'orange', 'red' (default: 'teal')
 * @param {string} variant - Style variant: 'default', 'subtle', 'bold', 'card' (default: 'default')
 * @param {string} emojiSize - Size of emoji: 'text-xl', 'text-2xl', 'text-3xl', 'text-4xl', 'text-5xl', 'text-6xl', 'text-7xl' (default: 'text-2xl')
 * @param {string} className - Additional CSS classes
 * @param {boolean} inline - If true, displays emoji and content inline (default: false)
 */
const Highlights = ({ 
  emoji, 
  children, 
  color = 'teal', 
  variant = 'default',
  className = '',
  emojiSize = 'text-2xl',
  inline = false
}) => {
  // Color mapping with gradient backgrounds and borders
  const colorMap = {
    teal: {
      bg: 'from-teal-50 to-cyan-50 dark:from-teal-900/20 dark:to-cyan-900/20',
      border: 'border-teal-200 dark:border-teal-800',
      borderLeft: 'border-teal-400 dark:border-teal-600'
    },
    blue: {
      bg: 'from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20',
      border: 'border-blue-200 dark:border-blue-800',
      borderLeft: 'border-blue-400 dark:border-blue-600'
    },
    green: {
      bg: 'from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20',
      border: 'border-green-200 dark:border-green-800',
      borderLeft: 'border-green-400 dark:border-green-600'
    },
    purple: {
      bg: 'from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20',
      border: 'border-purple-200 dark:border-purple-800',
      borderLeft: 'border-purple-400 dark:border-purple-600'
    },
    amber: {
      bg: 'from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20',
      border: 'border-amber-200 dark:border-amber-800',
      borderLeft: 'border-amber-400 dark:border-amber-600'
    },
    orange: {
      bg: 'from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20',
      border: 'border-orange-200 dark:border-orange-800',
      borderLeft: 'border-orange-400 dark:border-orange-600'
    },
    red: {
      bg: 'from-red-50 to-rose-50 dark:from-red-900/20 dark:to-rose-900/20',
      border: 'border-red-200 dark:border-red-800',
      borderLeft: 'border-red-400 dark:border-red-600'
    },
    indigo: {
      bg: 'from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20',
      border: 'border-indigo-200 dark:border-indigo-800',
      borderLeft: 'border-indigo-400 dark:border-indigo-600'
    }
  };

  const colors = colorMap[color] || colorMap.teal;

  // Variant styles
  const variantStyles = {
    default: `bg-gradient-to-br ${colors.bg} rounded-xl p-4 border-2 ${colors.border}`,
    subtle: `bg-gradient-to-r ${colors.bg} rounded-lg p-3 border-l-4 ${colors.borderLeft}`,
    bold: `bg-gradient-to-br ${colors.bg} rounded-2xl p-6 border-2 ${colors.border} shadow-md`,
    card: `bg-gradient-to-br ${colors.bg} rounded-2xl p-8 border-2 ${colors.border} shadow-lg`
  };

  const baseStyle = variantStyles[variant] || variantStyles.default;

  // Inline layout (emoji and content side by side)
  if (inline) {
    return (
      <div className={`${baseStyle} ${className}`}>
        <div className="flex items-center gap-2">
          {emoji && <span className={emojiSize}>{emoji}</span>}
          <div className="flex-1">
            {children}
          </div>
        </div>
      </div>
    );
  }

  // Default layout (emoji and content stacked or flexible)
  return (
    <div className={`${baseStyle} ${className}`}>
      {emoji && (
        <div className="flex justify-center mb-3">
          <span className={emojiSize}>{emoji}</span>
        </div>
      )}
      <div className="flex-1">
        {children}
      </div>
    </div>
  );
};

export default Highlights;
